import { useRouter } from 'next/navigation';
import { useState } from 'react';
import React from 'react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import {
  PersonsData,
  RoomsState,
  SearchBarWrapperSectionData,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import SearchBarPersonsDropdown from '@/components/organisms/search-bar/shared/search-bar-persons-dropdown/search-bar-persons-dropdown';
import Modal, { ModalProps } from '@/components/utilities/modal/modal';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { usePredefinedSearchContext } from '@/libraries/contexts/predefined-search-context/predefined-search-context';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './configuration-flow-pax-modal.module.scss';

export interface ConfigurationFlowPaxModalProps
  extends Omit<ModalProps, 'children'> {
  /** Search settings for the search bar */
  searchSettings: Content.Components['SearchSettingsDto'];
}

const ConfigurationFlowPaxModal = ({
  isOpen,
  setIsOpen,
  searchSettings,
  ...rest
}: ConfigurationFlowPaxModalProps) => {
  const { t } = useI18n();
  const router = useRouter();
  const locale = getLocale();
  const { configurationFlowAnchorLinkId } = usePageDetailsContext();
  const isTablet = useWindowSize('tablet');
  const { predefinedSearchParameters } = usePredefinedSearchContext();
  const isInPredefinedSearchPage = Boolean(predefinedSearchParameters);

  const paxDistribution = searchSettings.selectedPaxDistribution;
  const hasAutomaticRoomAssignment = !paxDistribution?.hasRoomDistribution;

  const roomsWithoutAutomaticRoomAssignment = Array.from(
    { length: paxDistribution?.rooms ?? 0 },
    (_, index) => ({
      childrenAges: paxDistribution?.childrenAgesPerRoom?.[index] ?? [],
      numberOfAdults: paxDistribution?.adultsPerRoom?.[index] ?? 2,
      numberOfChildren: paxDistribution?.childrenNoPerRoom?.[index] ?? 0,
    }),
  );

  const roomsWithAutomaticRoomAssignment = [
    {
      childrenAges: paxDistribution?.childrenAges || [],
      numberOfAdults: paxDistribution?.adultsNo || 2,
      numberOfChildren: paxDistribution?.childrenNo || 0,
    },
  ];

  const initialPersonsData = {
    dropdownData: {
      adultThreshold: searchSettings.adultAgeThreshold,
      defaultAdultNumber: searchSettings.defaultNumberOfAdultsPerRoom,
      maxPersons: searchSettings.maxNumberOfAdults,
      maxRooms: searchSettings.maxNumberOfRooms,
      roomDistributionWarning: searchSettings.roomDistributionWarning,
      roomsState: {
        automaticRoomAssignment: hasAutomaticRoomAssignment,
        rooms: hasAutomaticRoomAssignment
          ? roomsWithAutomaticRoomAssignment
          : roomsWithoutAutomaticRoomAssignment,
      },
    },
    id: 2,
    isLoading: false,
    // TODO: replace with data from search settings when available
    label: t('items.search.searchBar.persons.personsTitle'),
    placeholder: t('items.search.searchBar.persons.personsPlaceholder'),
    type: SearchBarWrapperSectionType.PERSONS,
    value: paxDistribution?.adultsNo
      ? `${paxDistribution?.adultsNo} ${t('items.search.searchBar.persons.adults')}`
      : undefined,
  };

  const [personsSection, setPersonsSection] =
    useState<SearchBarWrapperSectionData>(initialPersonsData);

  const handleChangePersons = (roomsState: RoomsState) => {
    let valueString: string;
    const totalAdults = roomsState.rooms.reduce(
      (sum, room) => sum + room.numberOfAdults,
      0,
    );
    const totalChildren = roomsState.rooms.reduce(
      (sum, room) => sum + room.numberOfChildren,
      0,
    );

    if (totalChildren > 0) {
      valueString = `${totalAdults} ${t('items.search.searchBar.persons.adults')}, ${totalChildren} ${t('items.search.searchBar.persons.children')}`;
    } else {
      valueString = `${totalAdults} ${t('items.search.searchBar.persons.adults')}`;
    }

    setPersonsSection(previousSection => {
      return {
        ...previousSection,
        dropdownData: {
          ...(previousSection.dropdownData as PersonsData),
          roomsState: roomsState,
        },
        isLoading: false,
        value: valueString,
      };
    });
  };

  const handleCloseModal = () => {
    setPersonsSection(initialPersonsData);
    setIsOpen?.(false);
  };

  const isPersonsSectionValid = (): boolean => {
    const personsData = personsSection.dropdownData as PersonsData;

    if (!personsData.roomsState) {
      return false;
    }

    return personsData.roomsState?.rooms.every(room => {
      return (
        room.numberOfChildren === room.childrenAges.length &&
        room.childrenAges.every(age => age !== null)
      );
    });
  };

  const getAdultCount = (): string | undefined => {
    return (personsSection?.dropdownData as PersonsData).roomsState?.rooms
      .reduce((total, room) => {
        return total + room.numberOfAdults;
      }, 0)
      .toString();
  };

  const getChildrenAges = (): string | undefined => {
    const allAges = (
      personsSection?.dropdownData as PersonsData
    ).roomsState?.rooms
      .flatMap(room => room.childrenAges)
      .filter((age): age is number => age !== null && age !== undefined);

    return allAges && allAges.join(',');
  };

  const getAdultsPerRoom = (): string | undefined => {
    const adultsCounts = (
      personsSection?.dropdownData as PersonsData
    ).roomsState?.rooms.map(room => room?.numberOfAdults?.toString());

    return (adultsCounts && adultsCounts.join('|')) ?? '';
  };

  const getChildrenAgesPerRoom = (): string | undefined => {
    const childrenAgesPerRoom = (
      personsSection?.dropdownData as PersonsData
    ).roomsState?.rooms.map(room =>
      room.numberOfChildren > 0
        ? room.childrenAges.filter(age => age !== null).join(',')
        : '',
    );

    return (childrenAgesPerRoom && childrenAgesPerRoom.join('|')) ?? '';
  };

  const getAutomaticRoomAssignment = (): boolean | undefined => {
    const automaticRoomAssignment = (
      personsSection?.dropdownData as PersonsData
    ).roomsState?.automaticRoomAssignment;

    return automaticRoomAssignment;
  };

  const handleUpdateSearch = () => {
    const constructAndUpdateSearchUrl = () => {
      const childrenAges = getChildrenAges() as string;
      const adultsNumber = getAdultCount()?.toString() as string;
      const adultsPerRoom = getAdultsPerRoom() as string;
      const childrenAgesPerRoom = getChildrenAgesPerRoom() as string;

      const departureDate = searchSettings.selectedDepartureDate
        ? new Date(
            searchSettings.selectedDepartureDate.toString(),
          ).toDateString()
        : '';
      const departureIds = searchSettings.selectedDepartureId?.toString() || '';
      const destinationIds =
        (searchSettings.selectedDestinationIds &&
          (searchSettings.selectedDestinationIds
            .map(value => value.toString())
            .join(',') as string)) ||
        '';
      const durationType = searchSettings.selectedPeriodType || '';

      const parameters = isInPredefinedSearchPage
        ? new URLSearchParams()
        : new URLSearchParams({
            batchSize: searchSettings.batchSize.toString(),
            departureDate: departureDate,
            departureIds: departureIds,
            destinationIds: destinationIds,
            durationType,
            locale: getCulture(locale),
            sortBy: searchSettings.selectedSortBy ?? 'websitePriority',
          });

      if (getAutomaticRoomAssignment()) {
        parameters.append('childrenAges', childrenAges);
        parameters.append('adultsNumber', adultsNumber);
      } else {
        parameters.append('adultsPerRoom', adultsPerRoom);
        parameters.append('childrenAgesPerRoom', childrenAgesPerRoom);
      }

      const parametersObject: Record<string, any> = {};

      parameters.forEach((value, key) => {
        parametersObject[key] = value;
      });

      updateUrlWithParameters({
        anchorLinkId: configurationFlowAnchorLinkId,
        newParameters: parametersObject,
        overwrite: true,
        redirectUrl: searchSettings.searchPageUrl,
        router,
        shouldRefresh: true,
      });

      handleCloseModal();
    };

    if (isPersonsSectionValid()) {
      constructAndUpdateSearchUrl();
    }
  };

  const searchBarPersonsDropdownProps = {
    adultThreshold:
      (personsSection.dropdownData as PersonsData)?.adultThreshold ?? 0,
    defaultAdultNumber: (personsSection.dropdownData as PersonsData)
      ?.defaultAdultNumber,
    description: personsSection.value ?? personsSection.placeholder,
    isVisible: isOpen,
    isWidget: true,
    label: personsSection.label,
    maxPersons: (personsSection.dropdownData as PersonsData)?.maxPersons,
    maxRooms: (personsSection.dropdownData as PersonsData)?.maxRooms,
    onChange: handleChangePersons,
    onClickBack: handleCloseModal,
    onClickClose: handleCloseModal,
    onClickNext: handleUpdateSearch,
    roomDistributionWarning: (personsSection.dropdownData as PersonsData)
      ?.roomDistributionWarning,
    roomsState: (personsSection.dropdownData as PersonsData)?.roomsState,
  };

  if (!isOpen) return null;

  return (
    <React.Fragment>
      {isTablet ? (
        <Modal
          {...rest}
          className={styles.component}
          isOpen={isOpen}
          onClickOutside={handleCloseModal}
          setIsOpen={handleCloseModal}
        >
          <div className={styles.curtain}>
            <div className={styles.container}>
              <div className={styles.modalActions}>
                <Typography className={styles.text} tagStyle="bodyMedium">
                  {t('items.common.words.cancel')}
                </Typography>

                <Button
                  aria-label={t('items.common.words.cancel')}
                  className={styles.close}
                  icon="Close"
                  iconSize={16}
                  onClick={handleCloseModal}
                  variant="tertiary"
                />
              </div>

              <SearchBarPersonsDropdown {...searchBarPersonsDropdownProps} />

              <div className={styles.buttonWrapper}>
                <Button
                  onClick={handleUpdateSearch}
                  text={t('items.common.words.search')}
                  variant="primary"
                />
              </div>
            </div>
          </div>
        </Modal>
      ) : (
        isOpen && (
          <SearchBarPersonsDropdown {...searchBarPersonsDropdownProps} />
        )
      )}
    </React.Fragment>
  );
};

ConfigurationFlowPaxModal.displayName = 'ConfigurationFlowPaxModal';

export default ConfigurationFlowPaxModal;
